import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Siren, 
  Heart, 
  Baby, 
  HeartPulse, 
  Bone, 
  ScanLine, 
  FlaskConical, 
  Flower2,
  ArrowRight,
  Clock,
  Stethoscope,
  FileCheck,
  CalendarCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Siren: Siren,
  Heart: Heart,
  Baby: Baby,
  HeartPulse: HeartPulse,
  Bone: Bone,
  ScanLine: ScanLine,
  FlaskConical: FlaskConical,
  Flower2: Flower2,
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (err) {
        console.error("Error fetching services:", err);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 miami-grid relative">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#00f0ff]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#ff00ff]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">What We Offer</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
              Our Medical <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Comprehensive healthcare services across multiple specialties, delivered by experienced medical professionals using state-of-the-art technology.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Heart;
              return (
                <div
                  key={service.id}
                  data-testid={`service-detail-${service.id}`}
                  className="group relative bg-[#1a0b2e]/60 border border-[#4b0082] rounded-2xl p-6 transition-all duration-300 hover:border-transparent overflow-hidden"
                >
                  {/* Gradient border on hover */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ 
                      background: `linear-gradient(135deg, ${service.color}40, transparent)`,
                      padding: '1px'
                    }}
                  />
                  <div className="absolute inset-[1px] rounded-2xl bg-[#1a0b2e] group-hover:bg-[#1a0b2e]/90" />
                  
                  <div className="relative z-10">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${service.color}20` }}
                    >
                      <IconComponent 
                        className="w-7 h-7"
                        style={{ color: service.color }}
                      />
                    </div>
                    
                    <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-[#00f0ff] transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-5">
                      {service.description}
                    </p>
                    
                    <Link to="/appointment">
                      <Button
                        variant="ghost"
                        className="text-[#00f0ff] hover:text-white hover:bg-[#00f0ff]/10 p-0 h-auto font-medium"
                      >
                        Book Appointment
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Patient Experience</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                What to Expect
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Our comprehensive approach ensures you receive the best care at every step. From your first appointment to follow-up care, we're with you all the way.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Clock, label: "Same-Day Appointments", desc: "Quick access to care when you need it most" },
                  { icon: Stethoscope, label: "Advanced Diagnostics", desc: "On-site lab and imaging for faster results" },
                  { icon: FileCheck, label: "Specialist Referrals", desc: "Seamless coordination with our specialist team" },
                  { icon: CalendarCheck, label: "Follow-Up Care", desc: "Continuous monitoring and support" },
                ].map((item, index) => (
                  <div key={index} className="glass rounded-xl p-4">
                    <item.icon className="w-6 h-6 text-[#ff00ff] mb-3" />
                    <h4 className="text-white font-semibold text-sm mb-1">{item.label}</h4>
                    <p className="text-gray-400 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1666214280391-8ff5bd3c0bf0?w=600"
                alt="Medical team"
                className="rounded-3xl w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#0f0518] via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Payment Options</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Insurance & Payment Options
            </h2>
            <p className="text-gray-400">We accept most major insurance plans and offer flexible payment options</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "🏥",
                title: "Insurance Accepted",
                description: "We accept Medicare, Medicaid, and most major private insurance plans including Blue Cross, Aetna, UnitedHealthcare, and more.",
                color: "#00f0ff",
              },
              {
                icon: "💳",
                title: "Payment Plans",
                description: "Flexible payment plans available for uninsured patients and those with high-deductible plans.",
                color: "#ff00ff",
              },
              {
                icon: "📋",
                title: "Transparent Pricing",
                description: "Clear, upfront pricing with no hidden fees. We'll verify your insurance coverage before your visit.",
                color: "#ff9e00",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`insurance-card-${index}`}
                className="glass rounded-2xl p-8 text-center card-hover"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ color: item.color }}
                >
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-[#00f0ff]/10 to-[#ff00ff]/10 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f0ff] to-[#ff00ff]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Schedule an appointment with one of our specialists today.
          </p>
          <Link to="/appointment">
            <Button 
              data-testid="services-cta-appointment"
              className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
            >
              Book Your Appointment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
